import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Crown, RefreshCw } from 'lucide-react';

type CategoryKey = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'L';

interface Badge {
  id: number;
  name: string;
  category: CategoryKey;
}

interface CategoryStyle {
  letter: string;
  label: string;
  bgFrom: string;
  bgTo: string;
  border: string;
  numberBg: string;
  textColor: string;
  legendBg: string;
  legendRing: string;
}

const CATEGORY: Record<CategoryKey, CategoryStyle> = {
  A: { letter: 'A', label: '기초역량', bgFrom: '#DBEAFE', bgTo: '#BFDBFE', border: '#1E40AF', numberBg: '#2563EB', textColor: '#1E3A8A', legendBg: '#2563EB', legendRing: '#1E40AF' },
  B: { letter: 'B', label: '생애주기', bgFrom: '#D1FAE5', bgTo: '#A7F3D0', border: '#047857', numberBg: '#059669', textColor: '#064E3B', legendBg: '#059669', legendRing: '#047857' },
  C: { letter: 'C', label: '특수목적', bgFrom: '#CFFAFE', bgTo: '#A5F3FC', border: '#0E7490', numberBg: '#0891B2', textColor: '#164E63', legendBg: '#0891B2', legendRing: '#0E7490' },
  D: { letter: 'D', label: '스포츠', bgFrom: '#FFEDD5', bgTo: '#FED7AA', border: '#C2410C', numberBg: '#EA580C', textColor: '#7C2D12', legendBg: '#EA580C', legendRing: '#C2410C' },
  E: { letter: 'E', label: '지도자과정', bgFrom: '#EDE9FE', bgTo: '#DDD6FE', border: '#6D28D9', numberBg: '#7C3AED', textColor: '#4C1D95', legendBg: '#7C3AED', legendRing: '#6D28D9' },
  F: { letter: 'F', label: '커리어', bgFrom: '#FCE7F3', bgTo: '#FBCFE8', border: '#BE185D', numberBg: '#DB2777', textColor: '#831843', legendBg: '#DB2777', legendRing: '#BE185D' },
  L: { letter: '★', label: '레전더리', bgFrom: '#FEF3C7', bgTo: '#FDE68A', border: '#B45309', numberBg: '#D97706', textColor: '#78350F', legendBg: '#D97706', legendRing: '#B45309' },
};

const A_BADGES: Badge[] = [
  { id: 1, name: 'Scientific Strength Coach', category: 'A' },
  { id: 2, name: 'Biomechanical Lifting Specialist', category: 'A' },
  { id: 3, name: 'Functional Pilates Analyst', category: 'A' },
  { id: 4, name: 'Neuro Yoga Instructor', category: 'A' },
  { id: 5, name: 'Corrective Barre Coach', category: 'A' },
  { id: 6, name: 'Applied Movement Trainer', category: 'A' },
  { id: 7, name: 'Clinical CrossFit Coach', category: 'A' },
  { id: 8, name: 'Evidence Based Instructor', category: 'A' },
  { id: 9, name: 'Human Movement Artist', category: 'A' },
  { id: 10, name: 'Intelligent Programming Coach', category: 'A' },
  { id: 11, name: 'Precision Movement Instructor', category: 'A' },
  { id: 12, name: 'Adaptive Fitness Specialist', category: 'A' },
  { id: 13, name: 'Functional Strength Scientist', category: 'A' },
  { id: 14, name: 'Neuro Functional Trainer', category: 'A' },
  { id: 15, name: 'Rehabilitation Pilates Coach', category: 'A' },
  { id: 16, name: 'Clinical Yoga Specialist', category: 'A' },
  { id: 17, name: 'Athletic Anatomy Expert', category: 'A' },
  { id: 18, name: 'Performance Physiology Coach', category: 'A' },
  { id: 19, name: 'Kinetic Movement Specialist', category: 'A' },
  { id: 20, name: 'Master of Applied Movement', category: 'A' },
];

const B_BADGES: Badge[] = [
  { id: 21, name: 'Silver Pilates Specialist', category: 'B' },
  { id: 22, name: 'Kids Functional Coach', category: 'B' },
  { id: 23, name: 'Prenatal Yoga Expert', category: 'B' },
  { id: 24, name: 'Teen Athletic Coach', category: 'B' },
  { id: 25, name: 'Midlife Strength Specialist', category: 'B' },
  { id: 26, name: 'Active Aging Trainer', category: 'B' },
  { id: 27, name: 'Youth Performance Coach', category: 'B' },
  { id: 28, name: 'Ballet Growth Specialist', category: 'B' },
  { id: 29, name: 'Barre Wellness Instructor', category: 'B' },
  { id: 30, name: 'Prenatal Movement Therapist', category: 'B' },
  { id: 31, name: 'Family Fitness Director', category: 'B' },
  { id: 32, name: 'Senior Recovery Coach', category: 'B' },
  { id: 33, name: 'Adolescent Conditioning Coach', category: 'B' },
  { id: 34, name: 'Healthy Aging Specialist', category: 'B' },
  { id: 35, name: 'Pregnancy Recovery Coach', category: 'B' },
  { id: 36, name: 'Generational Movement Expert', category: 'B' },
  { id: 37, name: 'Lifespan Pilates Educator', category: 'B' },
  { id: 38, name: 'Holistic Aging Coach', category: 'B' },
  { id: 39, name: 'Movement Development Specialist', category: 'B' },
  { id: 40, name: 'Master of Lifespan Movement', category: 'B' },
];

const C_BADGES: Badge[] = [
  { id: 41, name: 'Athletic Recovery Specialist', category: 'C' },
  { id: 42, name: 'Elite Rehab Coach', category: 'C' },
  { id: 43, name: 'Injury Prevention Scientist', category: 'C' },
  { id: 44, name: 'Return To Play Specialist', category: 'C' },
  { id: 45, name: 'High Performance Recovery Coach', category: 'C' },
  { id: 46, name: 'Mental Recovery Mentor', category: 'C' },
  { id: 47, name: 'Corrective Athlete Specialist', category: 'C' },
  { id: 48, name: 'Athletic Longevity Coach', category: 'C' },
  { id: 49, name: 'Sports Medicine Trainer', category: 'C' },
  { id: 50, name: 'Functional Athlete Coach', category: 'C' },
  { id: 51, name: 'Combat Recovery Specialist', category: 'C' },
  { id: 52, name: 'Recovery Programming Expert', category: 'C' },
  { id: 53, name: 'Athletic Therapy Coach', category: 'C' },
  { id: 54, name: 'Performance Resilience Specialist', category: 'C' },
  { id: 55, name: 'Elite Movement Specialist', category: 'C' },
  { id: 56, name: 'Hybrid Performance Therapist', category: 'C' },
  { id: 57, name: 'Professional Athlete Care Expert', category: 'C' },
  { id: 58, name: 'Sports Recovery Master', category: 'C' },
  { id: 59, name: 'Human Performance Restorer', category: 'C' },
  { id: 60, name: 'Master of Athletic Recovery', category: 'C' },
];

const D_BADGES: Badge[] = [
  { id: 61, name: 'Educational Pilates Mentor', category: 'D' },
  { id: 62, name: 'Fitness Brand Strategist', category: 'D' },
  { id: 63, name: 'Performance Content Creator', category: 'D' },
  { id: 64, name: 'Sequence Design Specialist', category: 'D' },
  { id: 65, name: 'Movement Education Director', category: 'D' },
  { id: 66, name: 'Boutique Studio Operator', category: 'D' },
  { id: 67, name: 'Athletic Business Manager', category: 'D' },
  { id: 68, name: 'Functional Education Designer', category: 'D' },
  { id: 69, name: 'Rehab Studio Founder', category: 'D' },
  { id: 70, name: 'Premium Wellness Director', category: 'D' },
  { id: 71, name: 'Human Movement Educator', category: 'D' },
  { id: 72, name: 'Fitness Leadership Coach', category: 'D' },
  { id: 73, name: 'Scientific Educator', category: 'D' },
  { id: 74, name: 'Clinical Education Specialist', category: 'D' },
  { id: 75, name: 'Elite Coaching Director', category: 'D' },
  { id: 76, name: 'Mind Body Brand Builder', category: 'D' },
  { id: 77, name: 'Fitness Curriculum Architect', category: 'D' },
  { id: 78, name: 'High Performance Educator', category: 'D' },
  { id: 79, name: 'Movement Business Architect', category: 'D' },
  { id: 80, name: 'Master of Education & Leadership', category: 'D' },
];

const F_BADGES: Badge[] = [
  { id: 61, name: 'Fitmass Marketer', category: 'F' },
  { id: 62, name: 'Sequence Programmer', category: 'F' },
  { id: 63, name: 'Teaching Specialist', category: 'F' },
  { id: 64, name: 'Studio Operator', category: 'F' },
  { id: 65, name: 'Team Management Leader', category: 'F' },
  { id: 66, name: 'Fitness Brand Builder', category: 'F' },
  { id: 67, name: 'Master Educator', category: 'F' },
  { id: 68, name: 'Education Designer', category: 'F' },
  { id: 69, name: 'Fitness Business Manager', category: 'F' },
  { id: 70, name: 'Brand Communication Specialist', category: 'F' },
  { id: 71, name: 'Curriculum Architect', category: 'F' },
  { id: 72, name: 'Leadership Coach', category: 'F' },
  { id: 73, name: 'Fitness Entrepreneur', category: 'F' },
  { id: 74, name: 'Director of Education', category: 'F' },
];

const GRAPES_1: Badge[] = [
  { id: 81, name: 'Human Movement Architect', category: 'L' },
  { id: 82, name: 'The Restorer', category: 'L' },
  { id: 83, name: 'The Performance Scientist', category: 'L' },
  { id: 84, name: 'The Longevity Master', category: 'L' },
  { id: 85, name: 'The Hybrid Athlete Coach', category: 'L' },
];

const GRAPES_2: Badge[] = [
  { id: 86, name: 'The Clinical Instructor', category: 'L' },
  { id: 87, name: 'The Body Engineer', category: 'L' },
  { id: 88, name: 'The Mind Body Sage', category: 'L' },
  { id: 89, name: 'The Movement Therapist', category: 'L' },
  { id: 90, name: 'The Athletic Scholar', category: 'L' },
];

const GRAPES_3: Badge[] = [
  { id: 91, name: 'The Educator', category: 'L' },
  { id: 92, name: 'The Movement Scientist', category: 'L' },
  { id: 93, name: 'The Rehabilitation Master', category: 'L' },
  { id: 94, name: 'The Elite Human Coach', category: 'L' },
  { id: 95, name: 'The Integrated Specialist', category: 'L' },
];

const GRAPES_4: Badge[] = [
  { id: 96, name: 'The Grand Instructor', category: 'L' },
  { id: 97, name: 'The Master Strategist', category: 'L' },
  { id: 98, name: 'The Evolution Trainer', category: 'L' },
  { id: 99, name: 'The Transcendent Coach', category: 'L' },
];

const badgeKey = (b: Badge) => `${b.category}-${b.id}`;

// ===== 레이아웃 좌표 =====
const L = {
  W: 1400,
  H: 2000,
  vineY: 240,
  grapeX: [180, 460, 940, 1220],
  grapeTopY: 295,
  grapeGap: 78,
  masterCx: 700,
  masterCy: 450,
  masterR: 135,
  trunkTopY: 585, // masterCy + masterR
  splitY: 740,
  bucketCx: [180, 440, 700, 960, 1220],
  bucketHeadCy: 840,
  bucketHeadR: 70,
  bucketHeadTop: 770,
  bucketHeadBottom: 910,
  badgeFirstY: 980,
  badgeRowGap: 80,
  badgeColOff: 70,
  badgeHalfW: 55,
  badgeHalfH: 35,
  footerY: 1770,
};

// ===== 뱃지 노드 (트리 위에 절대 배치) =====
function PositionedBadge({
  cx,
  cy,
  badge,
  active,
}: {
  cx: number;
  cy: number;
  badge: Badge;
  active: boolean;
}) {
  const c = CATEGORY[badge.category];
  return (
    <div
      className={`absolute z-20 ${active ? 'sparkle-anim' : ''}`}
      style={{
        left: cx - L.badgeHalfW,
        top: cy - L.badgeHalfH,
        width: L.badgeHalfW * 2,
      }}
    >
      {active && (
        <div
          className="absolute inset-0 -m-2 pointer-events-none"
          style={{
            background: `radial-gradient(circle, ${c.numberBg}88 0%, transparent 70%)`,
            filter: 'blur(8px)',
          }}
        />
      )}
      <div
        className="relative flex flex-col items-center"
        style={{
          filter: active
            ? 'none'
            : 'grayscale(80%) opacity(0.45) brightness(1.1)',
        }}
      >
        <div
          className="relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shadow-md z-10"
          style={{
            background: active
              ? `linear-gradient(135deg, ${c.numberBg} 0%, ${c.border} 100%)`
              : `linear-gradient(135deg, #D6D3D1 0%, #A8A29E 100%)`,
            color: active ? '#FFFFFF' : '#57534E',
            boxShadow: active
              ? `0 0 14px ${c.numberBg}, 0 2px 4px rgba(0,0,0,0.2)`
              : '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          {badge.id}
          {active && (
            <Sparkles
              className="absolute -top-1 -right-1 w-3 h-3 text-yellow-300"
              style={{ filter: 'drop-shadow(0 0 4px #facc15)' }}
            />
          )}
        </div>
        <div
          className="-mt-2 px-2 pt-3 pb-1.5 rounded-2xl text-center min-h-[42px] flex items-center justify-center w-[110px] shadow-sm"
          style={{
            background: active
              ? `linear-gradient(180deg, ${c.bgFrom} 0%, ${c.bgTo} 100%)`
              : 'linear-gradient(180deg, #F5F5F4 0%, #E7E5E4 100%)',
            border: active ? `1.5px solid ${c.border}` : '1.5px solid #D6D3D1',
            color: active ? c.textColor : '#78716C',
          }}
        >
          <span className="text-[9px] leading-tight font-semibold">{badge.name}</span>
        </div>
      </div>
    </div>
  );
}

// ===== 마스터 100 (크게) =====
function MasterBadge({ active, cx, cy, r }: { active: boolean; cx: number; cy: number; r: number }) {
  return (
    <div
      className={`absolute z-30 ${active ? 'sparkle-anim' : ''}`}
      style={{
        left: cx - r,
        top: cy - r,
        width: r * 2,
        height: r * 2,
      }}
    >
      {/* 황금 오라 (활성화 시만) */}
      {active && (
        <div
          className="absolute inset-0 -m-8 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(250,204,21,0.65) 0%, rgba(245,158,11,0.25) 40%, transparent 70%)',
            filter: 'blur(12px)',
          }}
        />
      )}
      <div
        className="relative w-full h-full rounded-full flex flex-col items-center justify-center shadow-2xl"
        style={{
          background: active
            ? 'radial-gradient(circle at 30% 25%, #57534E 0%, #1C1917 60%, #0C0A09 100%)'
            : 'radial-gradient(circle at 30% 25%, #A8A29E 0%, #57534E 60%, #292524 100%)',
          border: active ? '5px solid #D97706' : '5px solid #78716C',
          boxShadow: active
            ? '0 0 50px #F59E0B, 0 0 100px rgba(251,191,36,0.6), inset 0 4px 12px rgba(255,255,255,0.15)'
            : '0 8px 16px rgba(0,0,0,0.25), inset 0 4px 12px rgba(255,255,255,0.08)',
          opacity: active ? 1 : 0.65,
        }}
      >
        {/* 하이라이트 */}
        <div
          className="absolute rounded-full bg-white/15"
          style={{ top: '12%', left: '20%', width: '40%', height: '18%', filter: 'blur(6px)' }}
        />
        <Crown
          className="text-yellow-400 mb-1"
          style={{
            width: r * 0.35,
            height: r * 0.35,
            filter: 'drop-shadow(0 0 6px #fbbf24)',
          }}
        />
        <div
          className="text-white font-black leading-none"
          style={{ fontSize: r * 0.55 }}
        >
          100
        </div>
        <div
          className="text-yellow-300 font-bold mt-2"
          style={{ fontSize: r * 0.12 }}
        >
          Grand Master
        </div>
        <div className="text-yellow-200/90 font-semibold" style={{ fontSize: r * 0.1 }}>
          of
        </div>
        <div className="text-yellow-300 font-bold" style={{ fontSize: r * 0.12 }}>
          Human Movement
        </div>
        <div className="text-yellow-100/70 mt-1" style={{ fontSize: r * 0.085 }}>
          (전체 카테고리 ALL 완료)
        </div>
      </div>
    </div>
  );
}

// ===== 버킷 헤더 (큰 색구슬) =====
function BucketHeader({
  cx,
  cy,
  r,
  letter,
  title,
  subtitle,
  range,
  cat,
}: {
  cx: number;
  cy: number;
  r: number;
  letter: string;
  title: string;
  subtitle: string;
  range: string;
  cat: CategoryKey;
}) {
  const c = CATEGORY[cat];
  return (
    <div
      className="absolute z-20"
      style={{ left: cx - r, top: cy - r, width: r * 2, height: r * 2 }}
    >
      <div
        className="relative w-full h-full rounded-full flex flex-col items-center justify-center shadow-xl overflow-hidden"
        style={{
          background: `radial-gradient(circle at 30% 25%, ${c.bgFrom}, ${c.numberBg} 60%, ${c.border})`,
          border: `3.5px solid ${c.border}`,
          boxShadow: `0 8px 16px rgba(0,0,0,0.2), inset 0 4px 8px rgba(255,255,255,0.2)`,
        }}
      >
        <div
          className="absolute rounded-full bg-white/35"
          style={{ top: '10%', left: '20%', width: '38%', height: '18%', filter: 'blur(4px)' }}
        />
        <div className="text-white font-black text-3xl drop-shadow z-10">{letter}</div>
        <div className="text-white text-[10px] font-bold text-center px-2 leading-tight z-10 mt-0.5">
          {title}
        </div>
        <div className="text-white text-[9px] font-semibold text-center px-1 leading-tight z-10">
          {subtitle}
        </div>
        <div className="text-white/90 text-[9px] font-bold z-10 mt-0.5">{range}</div>
      </div>
    </div>
  );
}

// ===== 메인 컴포넌트 =====
export default function AdminBadgeTreeTab() {
  const allBadges = useMemo(
    () => [
      ...A_BADGES,
      ...B_BADGES,
      ...C_BADGES,
      ...D_BADGES,
      ...F_BADGES,
      ...GRAPES_1,
      ...GRAPES_2,
      ...GRAPES_3,
      ...GRAPES_4,
      { id: 100, name: 'Grand Master of Human Movement', category: 'L' as const },
    ],
    []
  );

  const [activeSet, setActiveSet] = useState<Set<string>>(new Set());
  const [seed, setSeed] = useState(0);

  useEffect(() => {
    const next = new Set<string>();
    allBadges.forEach((b) => {
      if (Math.random() < 0.32) next.add(badgeKey(b));
    });
    setActiveSet(next);
  }, [seed, allBadges]);

  const activeCount = activeSet.size;
  const totalCount = allBadges.length;

  // 버킷 데이터
  const BUCKETS = [
    { letter: 'A', title: '기초역량 기반', subtitle: '하이브리드', range: '(1~20)', cat: 'A' as CategoryKey, badges: A_BADGES },
    { letter: 'B', title: '생애주기 기반', subtitle: '하이브리드', range: '(21~40)', cat: 'B' as CategoryKey, badges: B_BADGES },
    { letter: 'C', title: '특수목적 × 스포츠', subtitle: '하이브리드', range: '(41~60)', cat: 'C' as CategoryKey, badges: C_BADGES },
    { letter: 'D', title: '지도자과정 × 기초역량', subtitle: '하이브리드', range: '(61~80)', cat: 'D' as CategoryKey, badges: D_BADGES },
    { letter: 'F', title: '커리어 중심', subtitle: '하이브리드', range: '(61~80)', cat: 'F' as CategoryKey, badges: F_BADGES },
  ];

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* 페이지 헤더 */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">뱃지 트리 테스트</h1>
          <p className="text-sm text-gray-600 mt-1">
            이용자가 3단 카테고리를 채울 때마다 활성화되는 100개 업적 트리 미리보기
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
            <span className="text-sm text-gray-600">활성화: </span>
            <span className="font-bold text-accent">{activeCount}</span>
            <span className="text-sm text-gray-400"> / {totalCount}</span>
          </div>
          <button
            onClick={() => setSeed((s) => s + 1)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
          >
            <RefreshCw className="w-4 h-4" />
            랜덤 재생성
          </button>
        </div>
      </div>

      {/* 트리 캔버스 */}
      <div className="overflow-x-auto">
        <div
          className="relative rounded-2xl"
          style={{
            width: L.W,
            height: L.H,
            background: 'linear-gradient(180deg, #FEFCE8 0%, #FEF9C3 100%)',
            border: '1px solid #FDE68A',
          }}
        >
          {/* ===== 단일 SVG: 모든 나뭇가지 / 잎 ===== */}
          <svg
            className="absolute inset-0 pointer-events-none"
            width={L.W}
            height={L.H}
            viewBox={`0 0 ${L.W} ${L.H}`}
          >
            <defs>
              <linearGradient id="branchGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#92400E" />
                <stop offset="100%" stopColor="#78350F" />
              </linearGradient>
              <linearGradient id="trunkGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7C2D12" />
                <stop offset="100%" stopColor="#451A03" />
              </linearGradient>
              <linearGradient id="leafGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#84CC16" />
                <stop offset="100%" stopColor="#4D7C0F" />
              </linearGradient>
            </defs>

            {/* === 상단 메인 바인(덩굴) - 4개 클러스터를 부드럽게 연결 === */}
            <path
              d={`M 60 ${L.vineY + 10}
                  C 140 ${L.vineY - 20}, ${L.grapeX[0]} ${L.vineY - 5}, ${L.grapeX[0]} ${L.vineY}
                  C ${L.grapeX[0] + 80} ${L.vineY + 15}, ${L.grapeX[1] - 80} ${L.vineY - 10}, ${L.grapeX[1]} ${L.vineY}
                  C ${L.grapeX[1] + 100} ${L.vineY + 20}, ${L.masterCx - 80} ${L.vineY - 5}, ${L.masterCx} ${L.vineY}
                  C ${L.masterCx + 80} ${L.vineY + 5}, ${L.grapeX[2] - 100} ${L.vineY + 20}, ${L.grapeX[2]} ${L.vineY}
                  C ${L.grapeX[2] + 80} ${L.vineY - 10}, ${L.grapeX[3] - 80} ${L.vineY + 15}, ${L.grapeX[3]} ${L.vineY}
                  C ${L.grapeX[3] + 80} ${L.vineY - 5}, ${L.W - 140} ${L.vineY - 20}, ${L.W - 60} ${L.vineY + 10}`}
              stroke="url(#branchGrad)"
              strokeWidth="7"
              fill="none"
              strokeLinecap="round"
            />

            {/* 상단 바인 줄기 잎 장식 */}
            <g>
              <ellipse cx="120" cy={L.vineY - 12} rx="14" ry="6" fill="url(#leafGrad)" transform={`rotate(-25 120 ${L.vineY - 12})`} />
              <ellipse cx="320" cy={L.vineY + 18} rx="14" ry="6" fill="url(#leafGrad)" transform={`rotate(20 320 ${L.vineY + 18})`} />
              <ellipse cx="620" cy={L.vineY - 12} rx="14" ry="6" fill="url(#leafGrad)" transform={`rotate(-15 620 ${L.vineY - 12})`} />
              <ellipse cx="800" cy={L.vineY + 18} rx="14" ry="6" fill="url(#leafGrad)" transform={`rotate(15 800 ${L.vineY + 18})`} />
              <ellipse cx="1100" cy={L.vineY - 12} rx="14" ry="6" fill="url(#leafGrad)" transform={`rotate(-20 1100 ${L.vineY - 12})`} />
              <ellipse cx="1300" cy={L.vineY + 18} rx="14" ry="6" fill="url(#leafGrad)" transform={`rotate(25 1300 ${L.vineY + 18})`} />
            </g>

            {/* === 각 클러스터: 바인 → 첫 뱃지 줄기 + 클러스터 세로 줄기 (모든 뱃지 관통) === */}
            {[GRAPES_1, GRAPES_2, GRAPES_3, GRAPES_4].map((cluster, ci) => {
              const cx = L.grapeX[ci];
              const lastY = L.grapeTopY + (cluster.length - 1) * L.grapeGap;
              return (
                <g key={ci}>
                  {/* 바인에서 첫 뱃지까지 짧은 곡선 */}
                  <path
                    d={`M ${cx} ${L.vineY} Q ${cx + 4} ${L.vineY + 25}, ${cx} ${L.grapeTopY - 25}`}
                    stroke="url(#branchGrad)"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />
                  {/* 클러스터 세로 줄기 (뱃지들 뒤를 관통) */}
                  <line
                    x1={cx}
                    y1={L.grapeTopY - 25}
                    x2={cx}
                    y2={lastY + 25}
                    stroke="url(#branchGrad)"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                  />
                  {/* 클러스터 상단 잎 */}
                  <ellipse cx={cx + 12} cy={L.grapeTopY - 35} rx="10" ry="5" fill="url(#leafGrad)" transform={`rotate(35 ${cx + 12} ${L.grapeTopY - 35})`} />
                </g>
              );
            })}

            {/* === 바인 중앙 → 100 마스터 (굵은 곡선 줄기) === */}
            <path
              d={`M ${L.masterCx} ${L.vineY}
                  C ${L.masterCx - 30} ${L.vineY + 50},
                    ${L.masterCx + 20} ${L.vineY + 90},
                    ${L.masterCx} ${L.masterCy - L.masterR - 5}`}
              stroke="url(#trunkGrad)"
              strokeWidth="9"
              fill="none"
              strokeLinecap="round"
            />
            <ellipse cx={L.masterCx + 18} cy={L.vineY + 60} rx="12" ry="6" fill="url(#leafGrad)" transform={`rotate(40 ${L.masterCx + 18} ${L.vineY + 60})`} />
            <ellipse cx={L.masterCx - 18} cy={L.vineY + 100} rx="12" ry="6" fill="url(#leafGrad)" transform={`rotate(-30 ${L.masterCx - 18} ${L.vineY + 100})`} />

            {/* === 100 마스터 아래 트렁크 (5갈래로 갈라지기 전) === */}
            <path
              d={`M ${L.masterCx} ${L.trunkTopY}
                  C ${L.masterCx + 6} ${L.trunkTopY + 40},
                    ${L.masterCx - 6} ${L.trunkTopY + 80},
                    ${L.masterCx} ${L.splitY}`}
              stroke="url(#trunkGrad)"
              strokeWidth="12"
              fill="none"
              strokeLinecap="round"
            />

            {/* === 5갈래 분기 (스플릿 지점 → 각 버킷 헤더 상단) === */}
            {L.bucketCx.map((bx, idx) => {
              const targetY = L.bucketHeadTop - 4;
              return (
                <path
                  key={idx}
                  d={`M ${L.masterCx} ${L.splitY}
                      C ${L.masterCx} ${L.splitY + 28},
                        ${bx} ${L.splitY + 36},
                        ${bx} ${targetY}`}
                  stroke="url(#trunkGrad)"
                  strokeWidth={idx === 2 ? 8 : 6.5}
                  fill="none"
                  strokeLinecap="round"
                />
              );
            })}

            {/* 분기 지점 잎 장식 */}
            <ellipse cx={L.masterCx - 50} cy={L.splitY + 30} rx="13" ry="6" fill="url(#leafGrad)" transform={`rotate(-25 ${L.masterCx - 50} ${L.splitY + 30})`} />
            <ellipse cx={L.masterCx + 50} cy={L.splitY + 30} rx="13" ry="6" fill="url(#leafGrad)" transform={`rotate(25 ${L.masterCx + 50} ${L.splitY + 30})`} />
            <ellipse cx={L.masterCx - 220} cy={L.splitY + 30} rx="11" ry="5" fill="url(#leafGrad)" transform={`rotate(-15 ${L.masterCx - 220} ${L.splitY + 30})`} />
            <ellipse cx={L.masterCx + 220} cy={L.splitY + 30} rx="11" ry="5" fill="url(#leafGrad)" transform={`rotate(15 ${L.masterCx + 220} ${L.splitY + 30})`} />

            {/* === 각 버킷: 헤더 → 마지막 뱃지까지 세로 트렁크 + 뱃지 가지 === */}
            {BUCKETS.map((bucket, idx) => {
              const bx = L.bucketCx[idx];
              const rows = Math.ceil(bucket.badges.length / 2);
              const lastY = L.badgeFirstY + (rows - 1) * L.badgeRowGap;
              const cat = CATEGORY[bucket.cat];
              return (
                <g key={idx}>
                  {/* 트렁크 그림자 (살짝 진하게 깔아서 입체감) */}
                  <line
                    x1={bx + 1.5}
                    y1={L.bucketHeadBottom - 5}
                    x2={bx + 1.5}
                    y2={lastY + 40}
                    stroke="#3F1A03"
                    strokeWidth="11"
                    strokeLinecap="round"
                    opacity="0.35"
                  />
                  {/* 버킷 헤더 아래 메인 세로 트렁크 (굵게) */}
                  <line
                    x1={bx}
                    y1={L.bucketHeadBottom - 8}
                    x2={bx}
                    y2={lastY + 38}
                    stroke="url(#trunkGrad)"
                    strokeWidth="9"
                    strokeLinecap="round"
                  />
                  {/* 트렁크 하이라이트 (왼쪽 면, 빛 받는 느낌) */}
                  <line
                    x1={bx - 2}
                    y1={L.bucketHeadBottom - 5}
                    x2={bx - 2}
                    y2={lastY + 35}
                    stroke="#A16207"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    opacity="0.6"
                  />
                  {/* 각 뱃지 가지 (트렁크 → 뱃지) */}
                  {bucket.badges.map((_, bi) => {
                    const row = Math.floor(bi / 2);
                    const col = bi % 2;
                    const by = L.badgeFirstY + row * L.badgeRowGap;
                    const direction = col === 0 ? -1 : 1;
                    const endX = bx + direction * 28;
                    return (
                      <g key={bi}>
                        {/* 가지 그림자 */}
                        <path
                          d={`M ${bx} ${by + 1} Q ${bx + direction * 10} ${by - 2}, ${endX} ${by + 1}`}
                          stroke="#3F1A03"
                          strokeWidth="5"
                          fill="none"
                          strokeLinecap="round"
                          opacity="0.3"
                        />
                        {/* 가지 본체 */}
                        <path
                          d={`M ${bx} ${by} Q ${bx + direction * 10} ${by - 3}, ${endX} ${by}`}
                          stroke="url(#branchGrad)"
                          strokeWidth="4"
                          fill="none"
                          strokeLinecap="round"
                        />
                        {/* 트렁크-가지 접합부 매듭 (knot) */}
                        <circle cx={bx} cy={by} r="3" fill="#451A03" />
                      </g>
                    );
                  })}
                  {/* 트렁크 잎 장식 */}
                  <ellipse cx={bx + 12} cy={L.bucketHeadBottom + 20} rx="11" ry="5" fill="url(#leafGrad)" transform={`rotate(35 ${bx + 12} ${L.bucketHeadBottom + 20})`} />
                  <ellipse cx={bx - 11} cy={L.bucketHeadBottom + 220} rx="10" ry="4.5" fill="url(#leafGrad)" transform={`rotate(-30 ${bx - 11} ${L.bucketHeadBottom + 220})`} />
                  <ellipse cx={bx + 11} cy={L.bucketHeadBottom + 460} rx="10" ry="4.5" fill="url(#leafGrad)" transform={`rotate(40 ${bx + 11} ${L.bucketHeadBottom + 460})`} />
                  <ellipse cx={bx - 12} cy={lastY - 5} rx="11" ry="5" fill="url(#leafGrad)" transform={`rotate(-35 ${bx - 12} ${lastY - 5})`} />
                  {/* 트렁크 최하단 작은 카테고리 컬러 매듭 */}
                  <circle cx={bx} cy={lastY + 38} r="6" fill={cat.numberBg} stroke={cat.border} strokeWidth="2" />
                </g>
              );
            })}
          </svg>

          {/* ===== 상단 타이틀 바 ===== */}
          <div
            className="absolute z-30"
            style={{ left: '50%', top: 20, transform: 'translateX(-50%)' }}
          >
            <div className="bg-gradient-to-b from-slate-800 to-slate-900 text-white px-12 py-3 rounded-full shadow-lg border-2 border-slate-700">
              <h2 className="text-xl font-black text-center">100개 업적 트리 전체 구조도</h2>
              <p className="text-xs text-center text-amber-300 font-semibold">
                5개 대분류 × 하이브리드 조합 100선
              </p>
            </div>
          </div>

          {/* ===== 범례 ===== */}
          <div
            className="absolute z-30 flex gap-4 flex-wrap justify-center"
            style={{ left: '50%', top: 130, transform: 'translateX(-50%)' }}
          >
            {(['A', 'B', 'C', 'D', 'E', 'F', 'L'] as CategoryKey[]).map((k) => {
              const c = CATEGORY[k];
              return (
                <div key={k} className="flex items-center gap-1.5">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm"
                    style={{
                      background: `radial-gradient(circle at 30% 30%, ${c.bgFrom}, ${c.legendBg})`,
                      border: `2px solid ${c.legendRing}`,
                    }}
                  >
                    {c.letter}
                  </div>
                  <span className="text-xs font-bold text-gray-700">{c.label}</span>
                </div>
              );
            })}
          </div>

          {/* ===== 4개 포도송이 클러스터 (절대 좌표) ===== */}
          {[GRAPES_1, GRAPES_2, GRAPES_3, GRAPES_4].map((cluster, ci) =>
            cluster.map((b, bi) => (
              <PositionedBadge
                key={badgeKey(b)}
                cx={L.grapeX[ci]}
                cy={L.grapeTopY + bi * L.grapeGap}
                badge={b}
                active={activeSet.has(badgeKey(b))}
              />
            ))
          )}

          {/* ===== 100 마스터 (대빵 크게) ===== */}
          <MasterBadge
            active={activeSet.has('L-100')}
            cx={L.masterCx}
            cy={L.masterCy}
            r={L.masterR}
          />

          {/* ===== 5개 버킷 헤더 + 뱃지 ===== */}
          {BUCKETS.map((bucket, idx) => {
            const bx = L.bucketCx[idx];
            return (
              <div key={idx}>
                <BucketHeader
                  cx={bx}
                  cy={L.bucketHeadCy}
                  r={L.bucketHeadR}
                  letter={bucket.letter}
                  title={bucket.title}
                  subtitle={bucket.subtitle}
                  range={bucket.range}
                  cat={bucket.cat}
                />
                {bucket.badges.map((b, bi) => {
                  const row = Math.floor(bi / 2);
                  const col = bi % 2;
                  const direction = col === 0 ? -1 : 1;
                  const cx = bx + direction * L.badgeColOff;
                  const cy = L.badgeFirstY + row * L.badgeRowGap;
                  return (
                    <PositionedBadge
                      key={badgeKey(b)}
                      cx={cx}
                      cy={cy}
                      badge={b}
                      active={activeSet.has(badgeKey(b))}
                    />
                  );
                })}
              </div>
            );
          })}

          {/* ===== 하단 조건 요약 ===== */}
          <div
            className="absolute z-20"
            style={{ left: 24, right: 24, top: L.footerY }}
          >
            <div className="pt-5 border-t-2 border-dashed border-amber-300">
              <div className="flex flex-wrap items-start justify-center gap-3">
                <div className="bg-white border-2 border-amber-400 rounded-xl p-3 shadow-sm">
                  <div className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
                    <Crown className="w-3.5 h-3.5" />
                    81~100 공통 조건
                  </div>
                  <ul className="text-[10px] text-gray-700 space-y-0.5 leading-tight">
                    <li>• 전체 업적 조합 달성</li>
                    <li>• 실습 경험 500시간 이상</li>
                    <li>• 교육/티칭 경험 100회 이상</li>
                    <li>• 관련 자격/인증 보유</li>
                    <li>• 심사 통과</li>
                  </ul>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="text-center text-xs font-bold text-gray-700 mb-1">
                    레전더리 칭호 조건 요약
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <ConditionCard nums="81, 91" label={'교육자/설계자\n관련 업적'} color="#A78BFA" />
                    <ConditionCard nums="82, 93, 94" label={'재활/회복/\n생애주기 관련 업적'} color="#34D399" />
                    <ConditionCard nums="83, 90, 95" label={'스포츠 퍼포먼스\n과학 관련 업적'} color="#FB923C" />
                    <ConditionCard nums="84, 85, 94" label={'하이브리드\n퍼포먼스 및\n코칭 관련 업적'} color="#FBBF24" />
                    <ConditionCard nums="96, 97" label={'지도자/교육/\n운영/브랜딩\n관련 업적'} color="#C084FC" />
                    <ConditionCard nums="98, 99, 100" label={'전체 업적 수량\n및 통합 조건\n달성'} color="#F87171" />
                  </div>
                </div>
              </div>
              <p className="text-center text-[10px] text-gray-500 mt-3">
                ※ 숫자 클릭 시 해당 업적의 상세 조건을 확인할 수 있습니다. (예시 UI)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 스타일 (반짝임 애니메이션) */}
      <style>{`
        @keyframes badgeSparkle {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        @keyframes masterPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
        .sparkle-anim {
          animation: badgeSparkle 2.4s ease-in-out infinite;
          transform-origin: center;
        }
      `}</style>
    </div>
  );
}

function ConditionCard({ nums, label, color }: { nums: string; label: string; color: string }) {
  return (
    <div
      className="rounded-lg px-2.5 py-2 shadow-sm border-2 w-[110px]"
      style={{ backgroundColor: `${color}22`, borderColor: color }}
    >
      <div className="text-xs font-bold text-center mb-1" style={{ color }}>
        {nums}
      </div>
      <div className="text-[9px] text-center text-gray-700 leading-tight whitespace-pre-line font-semibold">
        {label}
      </div>
    </div>
  );
}
